"use client";
import Typography from "@marginly/ui/components/typography";
import React, { useState } from "react";
import { useAccount } from "wagmi";

import Space from "@/components/common/space";
import { CONTEST_ID, COUNT_LINES } from "@/components/raffle/constants";
import {
  useGetContestRaffles,
  usePersonalTickets,
  useRequest,
} from "@/components/raffle/hooks";
import { PersonalTicketsModal } from "@/components/raffle/results/personal-tickets-modal/personal-tickets-modal";
import { WinnersModal } from "@/components/raffle/results/winners-modal/winners-modal";
import { getPlural } from "@/util/get-plural";
import { trackOpenRaffleResultModalClick } from "@/util/tracking";

import { TimerLabel } from "../timer-label";
import {
  RaffleCard,
  RafflesWrapper,
  Title,
  Wrapper,
  YourTickets,
} from "./raffle-results.styled";

function useGetMemberNumber(contestId: number) {
  return useRequest<number>(0, `contest/${contestId}/totalMemberNumber`);
}

export function RaffleResults() {
  const raffles = useGetContestRaffles(CONTEST_ID);
  const memberNumber = useGetMemberNumber(CONTEST_ID);
  const [openedRaffleIndex, setOpenedRaffleIndex] = useState<number | null>(
    null,
  );
  const [isMyTicketsOpen, setIsMyTicketsOpen] = useState(false);

  const { address } = useAccount();
  const personalTickets = usePersonalTickets(address, false);

  return (
    <>
      <Wrapper>
        <Title>
          <div className="title-container">
            <Space height={12} heightMobile={0} />
            <Typography headerL>See raffle results here</Typography>
            <Space height={12} heightMobile={4} />
            <Typography className="subheader">
              In completed raffle competitions.
            </Typography>
          </div>

          {!!personalTickets.length && (
            <>
              <YourTickets
                onClick={() => {
                  setIsMyTicketsOpen(true);
                }}
              >
                <img src="/images/ticket-black.svg" alt="" />
                <Typography action>Your Tickets</Typography>
              </YourTickets>
              {isMyTicketsOpen && (
                <PersonalTicketsModal
                  raffles={raffles}
                  tickets={personalTickets}
                  onHide={() => {
                    setIsMyTicketsOpen(false);
                  }}
                />
              )}
            </>
          )}
        </Title>
        <RafflesWrapper>
          {raffles.slice(0, 4).map((raffle, index) => (
            <RaffleCard
              key={raffle.raffleDate}
              $withWinners={!!raffle.winnersSelectedAt}
              onClick={() => {
                if (raffle.winnersSelectedAt) {
                  trackOpenRaffleResultModalClick();
                  setOpenedRaffleIndex(index);
                }
              }}
            >
              <Typography>{COUNT_LINES[index]} Raffle</Typography>
              {raffle.winnersSelectedAt ? (
                <Typography className="counter">
                  Show {getPlural(raffle.numberOfWinners, "Winner")}
                </Typography>
              ) : (
                <TimerLabel endTime={new Date(raffle.raffleDate)} />
              )}
            </RaffleCard>
          ))}
        </RafflesWrapper>
      </Wrapper>
      {openedRaffleIndex !== null && (
        <WinnersModal
          count={COUNT_LINES[openedRaffleIndex]}
          contestId={CONTEST_ID}
          raffleId={raffles[openedRaffleIndex].id}
          raffle={raffles[openedRaffleIndex]}
          memberNumber={memberNumber}
          onHide={() => {
            setOpenedRaffleIndex(null);
          }}
        />
      )}
    </>
  );
}
